import Styles from './styles.module.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { IoBookmark, IoBookmarkOutline, IoChatbubbleOutline, IoChevronDown, IoChevronUp, IoSettingsOutline } from 'react-icons/io5';
import { PiNotePencil, PiNotePencilFill } from 'react-icons/pi';
import {
  collection, doc, documentId, getDocs, getFirestore, increment, limit, onSnapshot,
  orderBy, query, Timestamp, updateDoc, where, writeBatch,
} from 'firebase/firestore';
import { useAppSelector } from 'src/salon/app/hook';
import StudentDetailPanel, { TabKey } from './detail/StudentDetailPanel';
import MemoPanel from '../6.manage/items/memo/index';
import EvaluationSettings from './settings/index';
import { unsavedGuard } from '../unsavedGuard';
import ClosePeriodModal from './ClosePeriodModal';
import {
  EVENT_KIND_LABELS, EVENT_STATUS_STEPS, EntryKind, EventEntry, eventShortLabel, eventStatusLabel, eventStatusTone,
  eventTargetLabel, eventTargetTitle, parseEventEntry,
  ApplicantRow, EntryRow, RecruitPeriod, VISIT_STATUS_CHOICES, VisitInfo, examinationLabel, examinationTone,
  parseRecruitPeriods, periodIndexFor, resolvePeriods, visitStatusLabel, visitStatusTone,
} from './types';
import type { ChatRoom } from '../6.manage/index';

// 応募・見学管理（左ナビ「応募者評価」/service/evaluation のトップページ）。
//
// 学生詳細は画面中央のモーダルやポップアップにはせず、一覧の右側に開く
// サイドパネル(StudentDetailPanel、detail/配下)として表示する。一覧は左側に残り、
// 一覧⇄詳細を行き来しやすいことを優先している。
//
// 一覧の取得元は applicants への直接クエリではなく chats コレクション
// （応募者管理 src/salon/desktop/6.manage/ と同じ company.id 絞り込み・並び替え。
// applicants への companyId 絞り込みクエリは本番での動作実績・インデックスがなく、
// 実際に一覧が空になる原因だったため使用しない）。評価固有のフィールドのみ
// applicants/{chats同一ID} からIDで直接取得して合成する。
//
// 一覧は「応募(chatType: application)」と「サロン見学(chatType: visit)」の
// 2種別を1つの表にまとめて表示する。見学は chats/{id}/visits/{visitId}
// （アプリ側のサロン見学フォームが書き込む事前アンケート回答）から進捗・詳細を取得する。
// この2種別に当てはまらないchat(通常のお問い合わせ等)は本画面の対象外として除外する。

const USERS_CHUNK = 30; // Firestore の documentId() 'in' クエリの上限
// applicants の list ルール(isApplicantCompanyAuthorized)は、in 句の候補1件ごとに会社ドキュメントを
// exists()/get() で調べる。1回のクエリで Rules が他ドキュメントを調べられる回数には上限があり、
// エミュレーターで確認すると15件は通り、20件では permission-denied になった。
// 30件ずつ読んでいた頃は、応募が16件を超える会社で一覧の補助データ取得が丸ごと失敗していたため、
// 余裕を見て10件ずつ読む。
const APPLICANTS_CHUNK = 10;

function toDate(value: any): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value) return new Date(value);
  return new Date();
}

function toChatRoom(id: string, data: Record<string, any>): ChatRoom {
  return {
    id,
    company: {
      id: data.company?.id ?? '',
      name: data.company?.name ?? '',
      recruitId: data.company?.recruitId ?? '',
      badge: data.company?.badge ?? 0,
    },
    user: {
      uid: data.user?.uid ?? '',
      name: data.user?.name ?? '',
      badge: data.user?.badge ?? 0,
    },
    chat: {
      lastMessage: data.chat?.lastMessage ?? '',
      lastSentAt: toDate(data.chat?.lastSentAt),
      chatStartedAt: toDate(data.chat?.chatStartedAt),
    },
    meta: {
      chatType: data.meta?.chatType ?? '',
      // 応募日(募集回の振り分けに使う)。古いチャットで無い場合は、今の時刻ではなくチャットの開始日にする
      createdAt: toDate(data.meta?.createdAt ?? data.chat?.chatStartedAt),
      status: data.meta?.status ?? '',
      memo: data.meta?.memo ?? '',
      save: data.meta?.save ?? false,
    },
  };
}

function toApplicantRow(chatRoom: ChatRoom, applicantData: Record<string, any> | undefined): ApplicantRow {
  const a = applicantData ?? {};
  return {
    id: chatRoom.id,
    recruitId: chatRoom.company.recruitId || (a.recruitId ?? ''),
    name: (a.basicInfo?.name || chatRoom.user.name || '').trim(),
    time: chatRoom.meta.createdAt,
    examination: typeof a.examination === 'number' ? a.examination : 1,
    memo: chatRoom.meta.memo,
    examList1: Array.isArray(a.examList1) ? a.examList1 : [],
    examList2: Array.isArray(a.examList2) ? a.examList2 : [],
    examList3: Array.isArray(a.examList3) ? a.examList3 : [],
    examList4: Array.isArray(a.examList4) ? a.examList4 : [],
    examList5: Array.isArray(a.examList5) ? a.examList5 : [],
  };
}

// companyId/statusのwhere条件は、Firestore Rules側(最新ルール.txt、
// applicants match /applicants/{applicantId}のallow list)がresource.data
// (companyId, status)を参照する条件になっているために必須。
// documentId() in [...]だけのクエリだと、Firestoreのlist query
// provability制約によりRules側の条件を証明できずpermission-deniedになる
// ことをEmulatorで確認済み(emulator_verification_report.txt
// applicants#8)。companyId/statusをwhere句自体に含めることで、
// クエリの絞り込み条件とRulesの許可条件を一致させている。
async function fetchApplicantsByIds(ids: string[], companyId: string): Promise<Map<string, Record<string, any>>> {
  const db = getFirestore();
  const map = new Map<string, Record<string, any>>();
  for (let i = 0; i < ids.length; i += APPLICANTS_CHUNK) {
    const chunk = ids.slice(i, i + APPLICANTS_CHUNK);
    if (chunk.length === 0) continue;
    const snap = await getDocs(query(
      collection(db, 'applicants'),
      where('companyId', '==', companyId),
      where('status', '==', true),
      where(documentId(), 'in', chunk),
    ));
    snap.docs.forEach((d) => map.set(d.id, d.data()));
  }
  return map;
}

// users/{uid}.education.school（学校側画面と同じフィールド）から学校名を取得する。
async function fetchSchoolsByUids(uids: string[]): Promise<Map<string, string>> {
  const db = getFirestore();
  const map = new Map<string, string>();
  const uniqueIds = Array.from(new Set(uids.filter(Boolean)));
  for (let i = 0; i < uniqueIds.length; i += USERS_CHUNK) {
    const chunk = uniqueIds.slice(i, i + USERS_CHUNK);
    if (chunk.length === 0) continue;
    const snap = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)));
    snap.docs.forEach((d) => map.set(d.id, d.data()?.education?.school ?? ''));
  }
  return map;
}

// chats/{chatId}/visits はサブコレクションのため、companyId横断のcollectionGroupクエリは
// インデックス未整備のリスクがある(applicants直接クエリで踏んだのと同じ問題)。
// 見学chatの件数は少ない想定のため、対象chatIdごとに個別取得する。
async function fetchVisitsByChatIds(chatIds: string[]): Promise<Map<string, VisitInfo>> {
  const db = getFirestore();
  const map = new Map<string, VisitInfo>();
  await Promise.all(chatIds.map(async (chatId) => {
    const snap = await getDocs(query(collection(db, 'chats', chatId, 'visits'), orderBy('requestedAt', 'desc'), limit(1)));
    if (snap.empty) return;
    const d = snap.docs[0];
    const data = d.data();
    map.set(chatId, {
      id: d.id,
      status: data.status ?? '',
      preferredDates: Array.isArray(data.preferredDates) ? data.preferredDates.map((t: any) => toDate(t)) : [],
      interestedTopics: Array.isArray(data.interestedTopics) ? data.interestedTopics : [],
      preferredStoreName: data.preferredStoreName ?? '',
    });
  }));
  return map;
}

const KIND_LABELS: Record<EntryKind, string> = { visit: 'サロン見学', application: '応募', infoSession: '会社説明会', coupon: 'クーポン' };
const kindBadgeClass = (kind: EntryKind): string =>
  kind === 'visit' ? Styles.kindBadgeVisit : kind === 'application' ? Styles.kindBadgeApplication : Styles.kindBadgeEvent;

// 説明会・クーポンそのもの(今の名前・日時・定員・受付の締め切り)
type EventTargetDoc = { id: string; label: string; startAt: Date | null; people: number; closed: boolean };

type AuxData = {
  applicants: Map<string, Record<string, any>>;
  schools: Map<string, string>;
  visits: Map<string, VisitInfo>;
};

const Evaluation: React.FC = () => {

  const company = useAppSelector((state) => state.company.company);

  const [ view, setView ] = useState<'list' | 'settings'>('list');
  // 応募・見学の行(chats から)。会社説明会・クーポンの行(eventEntries から)と合わせたものが entries
  const [ chatEntries, setEntries ] = useState<EntryRow[]>([]);
  // 会社の全チャット(会社説明会・クーポンの行から、その学生とのチャットを開くため)
  const [ chatRoomsById, setChatRoomsById ] = useState<Map<string, ChatRoom>>(new Map());
  // 会社説明会・クーポンの申込と、説明会・クーポンそのもの(今の名前・日時・定員・受付の締め切り)
  const [ eventEntries, setEventEntries ] = useState<EventEntry[]>([]);
  const [ sessionDocs, setSessionDocs ] = useState<Map<string, EventTargetDoc>>(new Map());
  const [ couponDocs, setCouponDocs ] = useState<Map<string, EventTargetDoc>>(new Map());
  // 会社説明会・クーポンのタブで選んでいる説明会・クーポン(null = すべて)
  const [ eventTargetFilter, setEventTargetFilter ] = useState<string | null>(null);
  const [ loading, setLoading ] = useState(true);
  const [ loadError, setLoadError ] = useState(false);
  const [ kindFilter, setKindFilter ] = useState<'' | EntryKind>('');
  const [ progressFilter, setProgressFilter ] = useState('');
  const [ searchText, setSearchText ] = useState('');
  // 募集回(応募設定の締切日)。募集回が2つ以上あるときは、種別タブの「応募」を「第1回応募」「第2回応募」…に分け、
  // 選んだ回の応募だけを出す。サロン見学は募集回と関係なく全員出す。
  // applicationPeriod が null の間は「まだ締めていない一番古い回」を選んでいる扱いにする。
  const [ recruitPeriods, setRecruitPeriods ] = useState<RecruitPeriod[]>(() => parseRecruitPeriods(undefined));
  const [ applicationPeriod, setApplicationPeriod ] = useState<number | null>(null);
  // 締める確認画面で締める回(開いた時点の回と人数で固定する。開いている間に別の画面で締められても、
  // 次の回の確認に変わらないように)
  const [ closingPeriod, setClosingPeriod ] = useState<{ index: number; name: string; counts: { hired: number; rejected: number; withdrawn: number; undecided: number } } | null>(null);
  // 前回読んだ補助データ。チャットの変更をすぐ表示するために使う(onSnapshot の中で更新する)。
  const auxRef = useRef<AuxData | null>(null);
  const [ selectedId, setSelectedId ] = useState<string | null>(null);
  // 一覧のメモアイコンから開くメモの編集(6.manage と同じ MemoPanel)
  const [ memoToggle, setMemoToggle ] = useState<{ id: string; name: string; memo: string } | null>(null);
  const [ initialTab, setInitialTab ] = useState<TabKey>('message');

  // 一覧の一括操作(チェックボックス選択→一括連絡送信／一括ステータス変更)
  const [ checkedIds, setCheckedIds ] = useState<Set<string>>(new Set());
  const [ bulkMessageOpen, setBulkMessageOpen ] = useState(false);
  const [ bulkMessageText, setBulkMessageText ] = useState('');
  const [ bulkSending, setBulkSending ] = useState(false);
  const [ bulkStatusValue, setBulkStatusValue ] = useState('');
  const [ bulkStatusSaving, setBulkStatusSaving ] = useState(false);

  useEffect(() => {
    if (!company?.id) return;

    setLoading(true);
    setLoadError(false);
    const db = getFirestore();
    const chatsQuery = query(
      collection(db, 'chats'),
      where('company.id', '==', company.id),
      orderBy('chat.lastSentAt', 'desc'),
    );

    // 一覧の行を、チャット(chats)と補助データ(応募データ・学校名・見学の申し込み)から組み立てる
    const buildRows = (relevant: ChatRoom[], aux: AuxData): EntryRow[] => relevant.map((c) => {
      const schoolName = aux.schools.get(c.user.uid) ?? '';

      if (c.meta.chatType === 'visit') {
        const visit = aux.visits.get(c.id) ?? null;
        return {
          id: c.id,
          kind: 'visit',
          chatRoom: c,
          name: c.user.name,
          schoolName,
          progressLabel: visitStatusLabel(visit?.status ?? ''),
          applicant: null,
          visit,
          event: null,
        };
      }

      const applicant = toApplicantRow(c, aux.applicants.get(c.id));
      return {
        id: c.id,
        kind: 'application',
        chatRoom: c,
        name: applicant.name || c.user.name,
        schoolName,
        progressLabel: examinationLabel(applicant.examination),
        applicant,
        visit: null,
        event: null,
      };
    });

    let latestSnapshot = 0;

    const unsubscribe = onSnapshot(
      chatsQuery,
      async (snap) => {
        const seq = ++latestSnapshot;
        const chatRooms = snap.docs.map((d) => toChatRoom(d.id, d.data()));
        setChatRoomsById(new Map(chatRooms.map((c) => [c.id, c])));

        // 本画面は「応募」「サロン見学」の2種別のみを扱う
        const relevant = chatRooms.filter((c) => c.meta.chatType === 'application' || c.meta.chatType === 'visit');
        const applicationChats = relevant.filter((c) => c.meta.chatType === 'application');
        const visitChats = relevant.filter((c) => c.meta.chatType === 'visit');

        // 1) 保存(★)・メモ・未読などチャット由来の変更は、前回読んだ補助データを使ってすぐ表示する
        //    (補助データの読み直しを待つと、全員分を読み終えるまで画面が変わらず遅く感じるため)。
        if (auxRef.current) {
          setEntries(buildRows(relevant, auxRef.current));
          setLoading(false);
        }

        // 2) 裏で補助データを読み直し、最新の内容で表示し直す。
        //    途中で次の変更が届いた場合は、古い読み込みの結果で上書きしない。
        // 補助データはそれぞれ独立して取得し、どれかが失敗しても他は表示に使う
        // (以前は Promise.all で1つでも失敗すると、見学の状態・学校名・選考段階がまとめて
        // 表示されなくなっていた)。
        const [ applicantsRes, schoolRes, visitRes ] = await Promise.allSettled([
          fetchApplicantsByIds(applicationChats.map((c) => c.id), company.id),
          // 学校名は会社説明会・クーポンだけの学生の分も読む
          fetchSchoolsByUids(chatRooms.map((c) => c.user.uid)),
          fetchVisitsByChatIds(visitChats.map((c) => c.id)),
        ]);
        if (seq !== latestSnapshot) return;

        const settled = <T,>(r: PromiseSettledResult<Map<string, T>>, label: string, previous?: Map<string, T>): Map<string, T> => {
          if (r.status === 'fulfilled') return r.value;
          console.error(`evaluation ${label} fetch error:`, r.reason);
          // 失敗したものは前回読めた内容を使う(無ければ空)
          return previous ?? new Map<string, T>();
        };
        const aux: AuxData = {
          applicants: settled(applicantsRes, 'applicants', auxRef.current?.applicants),
          schools: settled(schoolRes, 'schools', auxRef.current?.schools),
          visits: settled(visitRes, 'visits', auxRef.current?.visits),
        };
        auxRef.current = aux;

        setEntries(buildRows(relevant, aux));
        setLoading(false);
      },
      (error) => {
        console.error('evaluation chats fetch error:', error);
        setLoading(false);
        setLoadError(true);
      },
    );

    return () => unsubscribe();
  }, [company?.id]);

  // ── 会社説明会・クーポン ──
  useEffect(() => {
    if (!company?.id) return;
    const db = getFirestore();
    const unsubs = [
      onSnapshot(
        query(collection(db, 'eventEntries'), where('companyId', '==', company.id)),
        (snap) => setEventEntries(snap.docs.map((d) => parseEventEntry(d.id, d.data()))),
        (error) => console.error('会社説明会・クーポンの申込を読み込めませんでした', error),
      ),
      onSnapshot(
        query(collection(db, 'infoSessions'), where('companyId', '==', company.id)),
        (snap) => setSessionDocs(new Map(snap.docs.map((d) => {
          const x = d.data();
          const startAt = x.schedule?.startAt?.toDate ? x.schedule.startAt.toDate() : null;
          return [d.id, { id: d.id, label: eventTargetTitle({ kind: 'infoSession', target: { name: x.name } }), startAt, people: Number(x.people) || 0, closed: x.closed === true }];
        }))),
        (error) => console.error('会社説明会を読み込めませんでした', error),
      ),
      onSnapshot(
        query(collection(db, 'coupons'), where('companyId', '==', company.id)),
        (snap) => setCouponDocs(new Map(snap.docs.map((d) => {
          const x = d.data();
          return [d.id, { id: d.id, label: eventTargetTitle({ kind: 'coupon', target: { treatment: x.treatment } }), startAt: null, people: 0, closed: false }];
        }))),
        (error) => console.error('クーポンを読み込めませんでした', error),
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, [company?.id]);

  // 会社説明会・クーポンの行。チャットが見つからない(古いデータ等)ときも、申込の内容から行を作る
  const eventRows = useMemo<EntryRow[]>(() => eventEntries.map((ev) => {
    const chatRoom = chatRoomsById.get(ev.chatId) ?? {
      id: ev.chatId,
      company: { id: ev.companyId, name: '', recruitId: ev.recruitId, badge: 0 },
      user: { uid: ev.uid, name: ev.studentName, badge: 0 },
      chat: { lastMessage: '', lastSentAt: ev.updatedAt, chatStartedAt: ev.createdAt },
      meta: { chatType: ev.kind, createdAt: ev.createdAt, status: '', memo: '', save: false },
    } as ChatRoom;
    return {
      id: `event:${ev.id}`,
      kind: ev.kind,
      // 申込日時は、その説明会・クーポンに申し込んだ日
      chatRoom: { ...chatRoom, meta: { ...chatRoom.meta, createdAt: ev.createdAt } },
      name: chatRoom.user.name || ev.studentName,
      schoolName: auxRef.current?.schools.get(ev.uid) ?? '',
      progressLabel: eventStatusLabel(ev.kind, ev.status),
      applicant: null,
      visit: null,
      event: ev,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }).sort((a, b) => b.event!.createdAt.getTime() - a.event!.createdAt.getTime()), [eventEntries, chatRoomsById, chatEntries]);

  const entries = useMemo(() => [...chatEntries, ...eventRows], [chatEntries, eventRows]);

  // 学生(チャット)ごとの会社説明会・クーポンの申込(一覧の行・詳細に「説明会 10/12」などの印を出す)
  const eventsByChat = useMemo(() => {
    const map = new Map<string, EventEntry[]>();
    eventEntries.forEach((ev) => {
      if (!map.has(ev.chatId)) map.set(ev.chatId, []);
      map.get(ev.chatId)!.push(ev);
    });
    return map;
  }, [eventEntries]);

  // 会社説明会・クーポンのタブで出す、説明会・クーポンごとの切り替え(申込があるもの + まだ申込が無い説明会・クーポン)
  const eventTargetOptions = useMemo(() => {
    if (kindFilter !== 'infoSession' && kindFilter !== 'coupon') return [];
    const docs = kindFilter === 'infoSession' ? sessionDocs : couponDocs;
    const ids = new Set<string>([...Array.from(docs.keys()), ...eventEntries.filter((ev) => ev.kind === kindFilter).map((ev) => ev.targetId)]);
    const now = Date.now();
    return Array.from(ids).map((id) => {
      const docInfo = docs.get(id);
      const entriesOf = eventEntries.filter((ev) => ev.kind === kindFilter && ev.targetId === id);
      const sample = entriesOf[0];
      const startAt = docInfo?.startAt ?? sample?.target.startAt ?? null;
      return {
        id,
        label: docInfo?.label ?? (sample ? eventTargetTitle(sample) : '(削除された説明会・クーポン)'),
        active: entriesOf.filter((ev) => ev.status !== 'cancelled').length,
        people: docInfo?.people ?? 0,
        closed: docInfo?.closed ?? false,
        ended: kindFilter === 'infoSession' && !!startAt && startAt.getTime() < now,
        deleted: !docInfo,
        startAt,
      };
    }).sort((a, b) => (a.startAt && b.startAt ? b.startAt.getTime() - a.startAt.getTime() : b.active - a.active));
  }, [kindFilter, sessionDocs, couponDocs, eventEntries]);

  // 応募設定で締切日を変えたらすぐ反映されるよう、求人のドキュメントを購読する
  useEffect(() => {
    if (!company?.id) return;
    return onSnapshot(
      doc(getFirestore(), 'recruits', `${company.id}_2027`),
      (snap) => setRecruitPeriods(parseRecruitPeriods(snap.data()?.recruitPeriods)),
      (error) => console.error('募集回を読み込めませんでした', error),
    );
  }, [company?.id]);

  const periods = useMemo(() => resolvePeriods(recruitPeriods), [recruitPeriods]);
  // 応募がどの募集回に入るか(応募日で決める)
  const periodOf = useMemo(() => {
    const map = new Map<string, number>();
    entries.forEach((e) => {
      if (e.kind === 'application') map.set(e.id, periodIndexFor(periods, e.chatRoom.meta.createdAt));
    });
    return map;
  }, [entries, periods]);
  const periodCounts = useMemo(() => {
    const counts = periods.map(() => 0);
    periodOf.forEach((i) => { counts[i] = (counts[i] ?? 0) + 1; });
    return counts;
  }, [periods, periodOf]);
  // タブに出す回。まだ受付が始まっていない回は、応募がある場合だけ出す。
  const periodOptions = periods.filter((p) => p.state !== 'upcoming' || periodCounts[p.index] > 0);
  const multiPeriod = periodOptions.length > 1;
  const defaultPeriod = (periodOptions.find((p) => p.state !== 'closed') ?? periodOptions[periodOptions.length - 1])?.index ?? 0;
  // 一覧で絞り込む回('all' は絞り込まない)。回ごとのタブを選んでいるときだけ絞る。
  const selectedPeriod: number | 'all' = kindFilter === 'application' && multiPeriod
    ? (applicationPeriod !== null && periodOptions.some((p) => p.index === applicationPeriod) ? applicationPeriod : defaultPeriod)
    : 'all';

  // 締められるのは、まだ締めていない一番古い回だけ(受付が始まっていない回は除く)。ownerのみ。
  // ボタンは種別タブの行の右端に置き、どのタブを開いていてもその回を締める。
  const firstOpenPeriod = periods.find((p) => p.state !== 'closed');
  const closeTarget = company.role !== 'judge' && firstOpenPeriod && firstOpenPeriod.state !== 'upcoming' ? firstOpenPeriod : null;
  const closeCounts = useMemo(() => {
    const counts = { hired: 0, rejected: 0, withdrawn: 0, undecided: 0 };
    entries.forEach((e) => {
      if (e.kind !== 'application' || periodOf.get(e.id) !== closeTarget?.index) return;
      const exam = e.applicant?.examination;
      if (exam === 6) counts.hired++;
      else if (exam === 7) counts.rejected++;
      else if (exam === 8) counts.withdrawn++;
      else counts.undecided++;
    });
    return counts;
  }, [entries, periodOf, closeTarget?.index]);
  // 応募が締めた募集回に入っていれば、その回の名前(締めていなければ '')。
  // 締めた回の学生は選考段階を動かせない(結論なしの学生だけ、採用・不採用・辞退を選べる)。まとめての変更の対象にもしない。
  const closedPeriodName = (e: EntryRow): string => {
    if (e.kind !== 'application') return '';
    const p = periods[periodOf.get(e.id) ?? -1];
    return p?.state === 'closed' ? p.name : '';
  };

  const textSearched = useMemo(() => {
    const text = searchText.trim();
    if (!text) return entries;
    return entries.filter((e) => (e.name + e.chatRoom.meta.memo + e.schoolName).includes(text));
  }, [entries, searchText]);
  // 締めた募集回の応募か。「すべて」には出さない(締めた回は「応募」の回ごとの切り替えから見る)
  const isInClosedPeriod = (e: EntryRow): boolean =>
    e.kind === 'application' && periods[periodOf.get(e.id) ?? -1]?.state === 'closed';

  const searched = useMemo(
    () => {
      const inPeriod = selectedPeriod !== 'all'
        ? textSearched.filter((e) => e.kind !== 'application' || periodOf.get(e.id) === selectedPeriod)
        : kindFilter === ''
          ? textSearched.filter((e) => !isInClosedPeriod(e))
          : textSearched;
      // 会社説明会・クーポンのタブで説明会・クーポンを選んでいるときは、その申込だけ
      return eventTargetFilter && (kindFilter === 'infoSession' || kindFilter === 'coupon')
        ? inPeriod.filter((e) => !e.event || e.event.targetId === eventTargetFilter)
        : inPeriod;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [textSearched, selectedPeriod, periodOf, eventTargetFilter, kindFilter, periods],
  );

  // 種別（応募／サロン見学）ごとに進捗の語彙が違う(「1次選考」⇔「日程調整中」等)ため、
  // 種別を絞ってから進捗の選択肢・件数・一覧を組み立てる。
  // タブの件数は、回で絞る前(検索語だけで絞った)の件数。
  const kindCounts = useMemo(() => ({
    // すべて = 締めていない募集回の応募 + サロン見学 + 会社説明会 + クーポン
    all: textSearched.filter((e) => !isInClosedPeriod(e)).length,
    visit: textSearched.filter((e) => e.kind === 'visit').length,
    application: textSearched.filter((e) => e.kind === 'application').length,
    infoSession: textSearched.filter((e) => e.kind === 'infoSession').length,
    coupon: textSearched.filter((e) => e.kind === 'coupon').length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [textSearched, periodOf, periods]);
  const periodTabCount = (index: number) => textSearched.filter((e) => e.kind === 'application' && periodOf.get(e.id) === index).length;
  // 募集回が複数あるとき、「応募」タブに出すのは今の募集回(まだ締めていない一番古い回)の人数。
  // 下の切り替えで締めた回を見ていても、タブは今の回のまま(タブを押すと今の回に戻る)。
  const applicationTabPeriod = defaultPeriod;

  const kindFiltered = useMemo(
    () => kindFilter === '' ? searched : searched.filter((e) => e.kind === kindFilter),
    [searched, kindFilter],
  );

  // 種別を「サロン見学」「応募」にしたときに出す、ステータスごとの絞り込みタブ(審査ページの選考段階タブと同じ形)。
  // 段階の並びは固定。4次・5次選考と見学の未設定は、該当する学生がいるときだけ出す。
  const progressTabs = useMemo(() => {
    if (kindFilter === '') return [];
    const labels = kindFilter === 'visit'
      ? ['未設定', ...VISIT_STATUS_CHOICES.map((c) => c.label)]
      : kindFilter === 'infoSession' || kindFilter === 'coupon'
        ? [...EVENT_STATUS_STEPS[kindFilter].map((st) => st.label), 'キャンセル']
        : [1, 2, 3, 4, 5, 6, 7, 8].map((n) => examinationLabel(n));
    const optional = new Set(['未設定', examinationLabel(4), examinationLabel(5)]);
    return labels
      .map((label) => ({ label, count: kindFiltered.filter((e) => e.progressLabel === label).length }))
      .filter((t) => !optional.has(t.label) || t.count > 0);
  }, [kindFilter, kindFiltered]);

  // 種別を切り替えたら、片方にしかない進捗ラベルが選ばれたまま残らないようリセットする。
  useEffect(() => {
    setProgressFilter('');
    setEventTargetFilter(null);
  }, [kindFilter]);

  // タブ・絞り込み・検索を変えたら、チェックを外す。見えなくなった学生が選ばれたまま残ると、
  // 一括送信・一括変更が画面に出ていない学生にもかかってしまうため。
  useEffect(() => {
    setCheckedIds(new Set());
    setBulkMessageOpen(false);
    setBulkMessageText('');
    setBulkStatusValue('');
  }, [kindFilter, selectedPeriod, progressFilter, searchText, eventTargetFilter]);

  // 進捗状況での並び替え。'' は元の並び(chat.lastSentAt 降順)。
  //   見学: 未設定 → 日程調整中 → 日程確定 → 見学済み → キャンセル
  //   応募: 1次選考 → … → 5次選考 → 採用 → 不採用 → 辞退
  // 同じ段階の中では元の並び(新しいメッセージ順)を保つ。
  const [ progressSort, setProgressSort ] = useState<'' | 'asc' | 'desc'>('');
  const progressRank = (e: EntryRow): number => {
    if (e.kind === 'visit') {
      return ({ requested: 1, confirmed: 2, completed: 3, cancelled: 4 } as Record<string, number>)[e.visit?.status ?? ''] ?? 0;
    }
    if (e.event) {
      return ({ requested: 1, confirmed: 2, done: 3, cancelled: 4 } as Record<string, number>)[e.event.status] ?? 0;
    }
    return e.applicant?.examination ?? 1;
  };
  const cycleProgressSort = () => setProgressSort((prev) => (prev === '' ? 'asc' : prev === 'asc' ? 'desc' : ''));

  const filteredByProgress = progressFilter === '' ? kindFiltered : kindFiltered.filter((e) => e.progressLabel === progressFilter);
  const visible = progressSort === ''
    ? filteredByProgress
    : filteredByProgress
      .map((e, i) => ({ e, i }))
      .sort((a, b) => (progressRank(a.e) - progressRank(b.e)) * (progressSort === 'asc' ? 1 : -1) || a.i - b.i)
      .map(({ e }) => e);
  const countForProgress = (label: string) => label === '' ? kindFiltered.length : kindFiltered.filter((e) => e.progressLabel === label).length;

  // 種別タブが「すべて」の間だけ、クーポン→会社説明会→サロン見学→応募の順に区切って見出しを挟む
  // (元の並び順=chat.lastSentAt降順は各グループ内で維持する)。
  const groupedVisible = useMemo(() => {
    if (kindFilter !== '') return [{ kind: kindFilter, rows: visible }] as { kind: EntryKind; rows: EntryRow[] }[];
    // タブと同じ並び(クーポン → 会社説明会 → サロン見学 → 応募)
    return (['coupon', 'infoSession', 'visit', 'application'] as EntryKind[])
      .map((kind) => ({ kind, rows: visible.filter((e) => e.kind === kind) }))
      .filter((g) => g.rows.length > 0);
  }, [visible, kindFilter]);

  // 「すべて」のときの見学/応募のまとまりの開閉(閉じたまとまりは見出しと件数だけ残す)
  const [ collapsedGroups, setCollapsedGroups ] = useState<Set<EntryKind>>(new Set());
  const toggleGroup = (kind: EntryKind) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(kind)) next.delete(kind); else next.add(kind);
    return next;
  });

  const selectedEntry = selectedId ? (entries.find((e) => e.id === selectedId) ?? null) : null;

  // 一覧の保存(★)。一覧は chats を onSnapshot で購読しているため、書き込めば表示にも反映される。
  async function toggleSave(chatId: string, next: boolean) {
    try {
      await updateDoc(doc(getFirestore(), 'chats', chatId), { 'meta.save': next });
    } catch (error) {
      console.error('保存の切り替えに失敗しました:', error);
    }
  }

  function openDetail(entryId: string, tab: TabKey) {
    setSelectedId(entryId);
    setInitialTab(tab);
  }

  // パネル内での変更(選考段階の更新など)を一覧のローカル状態にも反映する。
  // 一覧はchatsのonSnapshotのみを購読しており、applicantsドキュメントへの
  // 書き込みは監視していないため、書き込み側から明示的にパッチしてもらう。
  function applyApplicantPatch(id: string, patch: Partial<ApplicantRow>) {
    const cachedApplicant = auxRef.current?.applicants.get(id);
    if (cachedApplicant) auxRef.current!.applicants.set(id, { ...cachedApplicant, ...patch });
    setEntries((prev) => prev.map((e) => {
      if (e.id !== id || !e.applicant) return e;
      const nextApplicant = { ...e.applicant, ...patch };
      return { ...e, applicant: nextApplicant, progressLabel: examinationLabel(nextApplicant.examination) };
    }));
  }

  // chats/{id}/visits/{visitId}のstatus変更も同様に、サブコレクションはonSnapshotの
  // 監視対象外のため明示的にパッチする。
  function applyVisitStatusPatch(id: string, status: string) {
    const cachedVisit = auxRef.current?.visits.get(id);
    if (cachedVisit) auxRef.current!.visits.set(id, { ...cachedVisit, status });
    setEntries((prev) => prev.map((e) => {
      if (e.id !== id || !e.visit) return e;
      return { ...e, visit: { ...e.visit, status }, progressLabel: visitStatusLabel(status) };
    }));
  }

  // ── 一括操作 ──
  const checkedEntries = useMemo(
    () => entries.filter((e) => checkedIds.has(e.id)),
    [entries, checkedIds],
  );
  // 応募/サロン見学ではステータスの意味づけが異なるため、選択が両方混在している間は
  // 一括ステータス変更を無効化する(種別を揃えてもらう)。
  const checkedKinds = useMemo(() => new Set(checkedEntries.map((e) => e.kind)), [checkedEntries]);
  const bulkKind: EntryKind | null = checkedKinds.size === 1 ? [...checkedKinds][0] : null;
  const allVisibleChecked = visible.length > 0 && visible.every((e) => checkedIds.has(e.id));

  function toggleCheckOne(id: string) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleCheckAllVisible() {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) {
        visible.forEach((e) => next.delete(e.id));
      } else {
        visible.forEach((e) => next.add(e.id));
      }
      return next;
    });
  }

  function clearChecked() {
    setCheckedIds(new Set());
    setBulkMessageOpen(false);
    setBulkMessageText('');
    setBulkStatusValue('');
  }

  // MessageTab(detail/MessageTab.tsx)の送信処理と同じ書き込み内容を、選択件数分まとめて
  // 1つのバッチで実行する。
  async function sendBulkMessage() {
    const text = bulkMessageText.trim();
    if (!text || checkedEntries.length === 0) return;
    if (!window.confirm(`選択した${checkedEntries.length}人にメッセージを送信します。よろしいですか？`)) return;

    setBulkSending(true);
    try {
      const db = getFirestore();
      const batch = writeBatch(db);
      checkedEntries.forEach((e) => {
        const messageRef = doc(collection(db, 'chats', e.id, 'messages'));
        batch.set(messageRef, {
          send: company.id,
          receive: e.chatRoom.user.uid,
          text,
          time: new Date(),
          image: null,
        });
        batch.update(doc(db, 'chats', e.id), {
          'chat.lastMessage': text,
          'chat.lastSentAt': new Date(),
          'user.badge': increment(1),
        });
      });
      await batch.commit();
      clearChecked();
    } catch (error) {
      console.error('bulk message send error:', error);
    } finally {
      setBulkSending(false);
    }
  }

  // StudentDetailPanel(changeExamination/changeVisitStatus)と同じ書き込み内容を
  // 選択件数分まとめて1つのバッチで実行する。
  async function applyBulkStatus() {
    if (!bulkKind || !bulkStatusValue || checkedEntries.length === 0) return;

    // 締めた募集回の学生は、まとめての変更の対象にしない(締めた回の選考段階は動かせないため)
    const targets = bulkKind === 'application' ? checkedEntries.filter((e) => !closedPeriodName(e)) : checkedEntries;
    const skipped = checkedEntries.length - targets.length;
    if (targets.length === 0) {
      window.alert('選択した学生は、締めた募集回の学生のため変更できません。');
      return;
    }
    const statusLabel = bulkKind === 'application'
      ? examinationLabel(Number(bulkStatusValue))
      : bulkKind === 'infoSession' || bulkKind === 'coupon'
        ? eventStatusLabel(bulkKind, bulkStatusValue)
        : (VISIT_STATUS_CHOICES.find((c) => c.value === bulkStatusValue)?.label ?? bulkStatusValue);
    const skippedNote = skipped > 0 ? `\n（締めた募集回の学生${skipped}人は変更しません）` : '';
    if (!window.confirm(`選択した${targets.length}人を「${statusLabel}」にします。よろしいですか？${skippedNote}`)) return;

    setBulkStatusSaving(true);
    try {
      const db = getFirestore();
      const batch = writeBatch(db);

      if (bulkKind === 'application') {
        const value = Number(bulkStatusValue);
        targets.forEach((e) => {
          if (!e.applicant) return;
          batch.set(doc(db, 'applicants', e.applicant.id), { examination: value }, { merge: true });
        });
      } else if (bulkKind === 'infoSession' || bulkKind === 'coupon') {
        // 会社説明会・クーポンの申込は eventEntries を購読しているので、書き込めば一覧にも反映される
        checkedEntries.forEach((e) => {
          if (!e.event) return;
          batch.update(doc(db, 'eventEntries', e.event.id), { status: bulkStatusValue, updatedAt: new Date() });
        });
      } else {
        checkedEntries.forEach((e) => {
          if (!e.visit) return;
          batch.set(doc(db, 'chats', e.id, 'visits', e.visit.id), { status: bulkStatusValue }, { merge: true });
        });
      }

      await batch.commit();

      // 一覧はchats/applicantsどちらもonSnapshotで全項目を監視しているわけではないため、
      // 単発のステータス変更(applyApplicantPatch/applyVisitStatusPatch)と同様に
      // ローカル状態にも反映する。
      if (bulkKind === 'application') {
        const value = Number(bulkStatusValue);
        targets.forEach((e) => applyApplicantPatch(e.id, { examination: value }));
      } else if (bulkKind === 'visit') {
        checkedEntries.forEach((e) => applyVisitStatusPatch(e.id, bulkStatusValue));
      }

      clearChecked();
    } catch (error) {
      console.error('bulk status update error:', error);
    } finally {
      setBulkStatusSaving(false);
    }
  }

  return (
    <div>
      <div className={Styles.card}>
        <div className={Styles.topActionRow}>
          {company.role !== 'judge' && (
            <button
              type="button"
              className={view === 'settings' ? `${Styles.settingsLinkBtn} ${Styles.settingsLinkBtnActive}` : Styles.settingsLinkBtn}
              onClick={() => {
                // 応募設定は全幅で見せるため、右側の学生詳細(チャット)は閉じる。
                // 閉じて一覧に戻るときは、保存していない変更があれば確認を出す(unsavedGuard のモーダル)。
                if (view === 'settings') {
                  unsavedGuard.request(() => setView('list'));
                } else {
                  setSelectedId(null);
                  setView('settings');
                }
              }}
            >
              <IoSettingsOutline className={Styles.settingsLinkIcon} />
              応募設定
            </button>
          )}
        </div>

        {view === 'settings' && company.role !== 'judge' ? (
          <div className={Styles.body}>
            <EvaluationSettings />
          </div>
        ) : (
          <div className={Styles.body}>
            <div className={Styles.kindTabBar}>
              {([
                { value: '' as const, label: 'すべて', count: kindCounts.all },
                { value: 'coupon' as const, label: 'クーポン', count: kindCounts.coupon },
                { value: 'infoSession' as const, label: '会社説明会', count: kindCounts.infoSession },
                { value: 'visit' as const, label: 'サロン見学', count: kindCounts.visit },
                { value: 'application' as const, label: '応募', count: kindCounts.application },
              ]).map(({ value, label, count }) => (
                <button
                  key={label}
                  type="button"
                  className={kindFilter === value ? `${Styles.kindTab} ${Styles.kindTabActive}` : Styles.kindTab}
                  onClick={() => {
                    setKindFilter(value);
                    // 「応募」を押したら、今の募集回を開く
                    if (value === 'application') setApplicationPeriod(null);
                  }}
                >
                  {value === 'application' && multiPeriod
                    ? `${label}（第${applicationTabPeriod + 1}回 ${periodTabCount(applicationTabPeriod)}）`
                    : `${label}（${count}）`}
                </button>
              ))}
              {closeTarget && (
                <button type="button" className={Styles.periodCloseBtn} onClick={() => closeTarget && setClosingPeriod({ index: closeTarget.index, name: closeTarget.name, counts: closeCounts })}>
                  {multiPeriod ? `第${closeTarget.index + 1}回を締める` : 'この募集回を締める'}
                </button>
              )}
            </div>

            {/* 募集回が2つ以上あるときは、「応募」の下に回ごとの切り替えを出す */}
            {kindFilter === 'application' && multiPeriod && (
              <div className={Styles.periodTabs}>
                {/* 回の順(第1回 → 第2回 …)に並べる */}
                {periodOptions.map((p) => (
                  <button
                    key={p.index}
                    type="button"
                    className={selectedPeriod === p.index ? `${Styles.periodTab} ${Styles.periodTabActive}` : Styles.periodTab}
                    onClick={() => {
                      setApplicationPeriod(p.index);
                      setProgressFilter('');
                      setSelectedId(null);
                    }}
                  >
                    第{p.index + 1}回
                    <span className={Styles.periodTabCount}>{periodTabCount(p.index)}</span>
                    {p.state === 'closed' && <span className={Styles.periodTabClosed}>締め済み</span>}
                  </button>
                ))}
              </div>
            )}

            {/* 会社説明会・クーポンのタブでは、どの説明会・クーポンかで切り替える */}
            {(kindFilter === 'infoSession' || kindFilter === 'coupon') && eventTargetOptions.length > 0 && (
              <div className={Styles.periodTabs}>
                <button
                  type="button"
                  className={eventTargetFilter === null ? `${Styles.periodTab} ${Styles.periodTabActive}` : Styles.periodTab}
                  onClick={() => { setEventTargetFilter(null); setProgressFilter(''); setSelectedId(null); }}
                >
                  すべての{EVENT_KIND_LABELS[kindFilter]}
                </button>
                {eventTargetOptions.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    className={eventTargetFilter === o.id ? `${Styles.periodTab} ${Styles.periodTabActive}` : Styles.periodTab}
                    onClick={() => { setEventTargetFilter(o.id); setProgressFilter(''); setSelectedId(null); }}
                  >
                    {o.label}
                    <span className={Styles.periodTabCount}>
                      {kindFilter === 'infoSession' && o.people > 0 ? `申込${o.active}／定員${o.people}` : `申込${o.active}`}
                    </span>
                    {o.ended ? <span className={Styles.periodTabClosed}>終了</span>
                      : o.closed ? <span className={Styles.periodTabClosed}>受付終了</span>
                      : o.deleted ? <span className={Styles.periodTabClosed}>削除済み</span> : null}
                  </button>
                ))}
              </div>
            )}

            {progressTabs.length > 0 && (
              <div className={Styles.progressTabs}>
                {[{ label: '', count: countForProgress('') }, ...progressTabs].map(({ label, count }) => (
                  <button
                    key={label || 'all'}
                    type="button"
                    className={progressFilter === label ? Styles.progressTabActive : Styles.progressTab}
                    onClick={() => setProgressFilter(label)}
                  >
                    {label || (kindFilter === 'visit' ? '全ステータス' : '全段階')}
                    <span>{count}</span>
                  </button>
                ))}
              </div>
            )}

            <div className={Styles.filterBar}>
              <div className={Styles.filterSearchBox}>
                <FaSearch className={Styles.filterSearchIcon} />
                <input
                  placeholder="フリーワード検索"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
            </div>

            {checkedIds.size > 0 && (
              <div className={Styles.bulkActionBar}>
                <span className={Styles.bulkActionCount}>{checkedIds.size}件選択中</span>

                <button
                  type="button"
                  className={bulkMessageOpen ? Styles.bulkActionBtn : Styles.bulkActionBtnIdle}
                  onClick={() => setBulkMessageOpen((v) => !v)}
                >
                  一括で連絡を送信
                </button>

                {bulkKind ? (
                  <div className={Styles.bulkStatusGroup}>
                    <select
                      className={Styles.bulkStatusSelect}
                      value={bulkStatusValue}
                      onChange={(e) => setBulkStatusValue(e.target.value)}
                    >
                      <option value="">ステータスを選択</option>
                      {bulkKind === 'application'
                        ? [1, 2, 3, 4, 5, 6, 7, 8].map((v) => (
                          <option key={v} value={v}>{examinationLabel(v)}</option>
                        ))
                        : bulkKind === 'infoSession' || bulkKind === 'coupon'
                          ? [...EVENT_STATUS_STEPS[bulkKind], { value: 'cancelled', label: 'キャンセル' }].map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))
                          : VISIT_STATUS_CHOICES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                    </select>
                    <button
                      type="button"
                      className={Styles.bulkActionBtn}
                      onClick={applyBulkStatus}
                      disabled={!bulkStatusValue || bulkStatusSaving}
                    >
                      {bulkStatusSaving ? '変更中...' : '一括変更'}
                    </button>
                  </div>
                ) : (
                  <span className={Styles.bulkActionNote}>種別（応募／サロン見学／会社説明会／クーポン）が混在しているため、ステータスの一括変更はできません</span>
                )}

                <button type="button" className={Styles.bulkActionClear} onClick={clearChecked}>選択解除</button>

                {bulkMessageOpen && (
                  <div className={Styles.bulkMessageBox}>
                    <textarea
                      className={Styles.bulkMessageTextarea}
                      placeholder={`選択した${checkedIds.size}件全員に送信するメッセージを入力`}
                      value={bulkMessageText}
                      onChange={(e) => setBulkMessageText(e.target.value)}
                    />
                    <div className={Styles.bulkMessageButtons}>
                      <button
                        type="button"
                        className={Styles.bulkActionClear}
                        onClick={() => { setBulkMessageOpen(false); setBulkMessageText(''); }}
                      >
                        キャンセル
                      </button>
                      <button
                        type="button"
                        className={Styles.bulkActionBtn}
                        onClick={sendBulkMessage}
                        disabled={!bulkMessageText.trim() || bulkSending}
                      >
                        {bulkSending ? '送信中...' : `${checkedEntries.length}件に送信`}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 学生を開いたときは、タブ・検索欄は全幅のまま、一覧だけを縮めて右に詳細(チャット)を並べる */}
            <div className={selectedEntry ? Styles.splitRow : undefined}>
            <div className={selectedEntry ? Styles.splitList : undefined}>
            {loadError ? (
              <p className={Styles.empty}>応募・見学情報の取得に失敗しました。時間をおいて再度お試しください。</p>
            ) : loading ? (
              <p className={Styles.empty}>読み込み中...</p>
            ) : visible.length === 0 ? (
              <p className={Styles.empty}>該当する応募・見学がありません。</p>
            ) : (
              <div className={Styles.appTable}>
                <div className={Styles.appTableHeader}>
                  <span className={Styles.appColCheck}>
                    <input type="checkbox" checked={allVisibleChecked} onChange={toggleCheckAllVisible} />
                  </span>
                  <span className={Styles.appColKind}>種別</span>
                  <span className={Styles.appColName}>学生名</span>
                  <span className={Styles.appColProgress}>
                    <button
                      type="button"
                      className={progressSort ? `${Styles.sortHeader} ${Styles.sortHeaderActive}` : Styles.sortHeader}
                      onClick={cycleProgressSort}
                      title={progressSort === '' ? '進んでいない順に並べる' : progressSort === 'asc' ? '進んでいる順に並べる' : '元の並び(新しいメッセージ順)に戻す'}
                    >
                      進捗状況
                      <span className={Styles.sortArrow}>{progressSort === 'asc' ? '▲' : progressSort === 'desc' ? '▼' : '⇅'}</span>
                    </button>
                  </span>
                  {!selectedEntry && (
                    <>
                      <span className={Styles.appColSchool}>学校名</span>
                      <span className={Styles.appColTime}>申込日時</span>
                      <span className={Styles.appColMemo}>メモ</span>
                    </>
                  )}
                  {selectedEntry && <span className={Styles.appColMemoIcon} />}
                  <span className={Styles.appColSaveIcon} />
                  <span className={Styles.appColAction} />
                </div>
                {groupedVisible.map((group) => (
                  <React.Fragment key={group.kind}>
                    {kindFilter === '' && (
                      <div className={Styles.groupHeader}>
                        <span className={kindBadgeClass(group.kind)}>{KIND_LABELS[group.kind]}</span>
                        <span className={Styles.groupHeaderCount}>{group.rows.length}件</span>
                        <button
                          type="button"
                          className={Styles.groupToggle}
                          onClick={() => toggleGroup(group.kind)}
                          aria-expanded={!collapsedGroups.has(group.kind)}
                          title={collapsedGroups.has(group.kind) ? '開く' : '閉じる'}
                        >
                          {collapsedGroups.has(group.kind) ? <IoChevronDown size={14} /> : <IoChevronUp size={14} />}
                        </button>
                      </div>
                    )}
                    {/* 「すべて」のときだけ、見学/応募のまとまりごとに開閉できる */}
                    {!(kindFilter === '' && collapsedGroups.has(group.kind)) && group.rows.map((e) => (
                      <div
                        className={selectedId === e.id ? `${Styles.appRow} ${Styles.appRowActive}` : Styles.appRow}
                        key={e.id}
                        onClick={() => openDetail(e.id, 'message')}
                      >
                        <span className={Styles.appColCheck} onClick={(ev) => ev.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={checkedIds.has(e.id)}
                            onChange={() => toggleCheckOne(e.id)}
                          />
                        </span>
                        <span className={Styles.appColKind}>
                          <span className={kindBadgeClass(e.kind)}>{KIND_LABELS[e.kind]}</span>
                        </span>
                        <span className={Styles.appColName}>
                          <span className={Styles.appNameText}>{e.name || '名前未設定'}</span>
                          {/* 会社説明会・クーポンの行は、どの説明会・クーポンか。応募・見学の行は、その学生の説明会・クーポンの申込 */}
                          {e.event ? (
                            <span className={Styles.eventTargetText}>{eventTargetLabel(e.event)}</span>
                          ) : (eventsByChat.get(e.chatRoom.id) ?? []).length > 0 && (
                            <span className={Styles.eventChips}>
                              {(eventsByChat.get(e.chatRoom.id) ?? []).map((ev) => (
                                <span key={ev.id} className={ev.status === 'cancelled' ? `${Styles.eventChip} ${Styles.eventChipCancelled}` : Styles.eventChip}>
                                  {eventShortLabel(ev)}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                        <span className={Styles.appColProgress}>
                          <span className={`${Styles.stageBadge} ${Styles[`tone_${e.event ? eventStatusTone(e.event.status) : e.kind === 'visit' ? visitStatusTone(e.visit?.status) : examinationTone(e.applicant?.examination)}`]}`}>{e.progressLabel}</span>
                        </span>
                        {!selectedEntry && (
                          <>
                            <span className={Styles.appColSchool}>{e.schoolName || '-'}</span>
                            <span className={Styles.appColTime}>
                              {/* 申込日時は応募・見学を申し込んだ日(meta.createdAt)。見学から応募した学生・再応募した学生は応募した日になる */}
                              {e.chatRoom.meta.createdAt.getFullYear()}年{e.chatRoom.meta.createdAt.getMonth() + 1}月{e.chatRoom.meta.createdAt.getDate()}日
                            </span>
                            {/* メモ(押すと書ける。詳細を開いているときは右のメモアイコンから) */}
                            <span
                              className={Styles.appColMemo}
                              onClick={(ev) => { ev.stopPropagation(); setMemoToggle({ id: e.chatRoom.id, name: e.name, memo: e.chatRoom.meta.memo }); }}
                              title={e.chatRoom.meta.memo ? 'メモを編集する' : 'メモを書く'}
                            >
                              {e.chatRoom.meta.memo ? (
                                <>
                                  <span className={Styles.memoText}>{e.chatRoom.meta.memo}</span>
                                  <PiNotePencilFill className={Styles.memoEditIcon} />
                                </>
                              ) : (
                                <span className={Styles.memoAdd}><PiNotePencil />メモを書く</span>
                              )}
                            </span>
                          </>
                        )}
                        {/* 詳細を開いて一覧が狭いときはメモ列が消えるため、メモはアイコンから開く */}
                        {selectedEntry && (
                          <span className={Styles.appColMemoIcon}>
                            {e.chatRoom.meta.memo ? (
                              <PiNotePencilFill
                                className={Styles.appActionIconActive}
                                title={e.chatRoom.meta.memo}
                                onClick={(ev) => { ev.stopPropagation(); setMemoToggle({ id: e.chatRoom.id, name: e.name, memo: e.chatRoom.meta.memo }); }}
                              />
                            ) : (
                              <PiNotePencil
                                className={Styles.appActionIcon}
                                title="メモを書く"
                                onClick={(ev) => { ev.stopPropagation(); setMemoToggle({ id: e.chatRoom.id, name: e.name, memo: '' }); }}
                              />
                            )}
                          </span>
                        )}
                        {/* 保存(★)。詳細上部の★と同じ chats.meta.save を切り替える */}
                        <span className={Styles.appColSaveIcon}>
                          {e.chatRoom.meta.save ? (
                            <IoBookmark
                              className={Styles.appSaveIconActive}
                              title="保存を外す"
                              onClick={(ev) => { ev.stopPropagation(); toggleSave(e.chatRoom.id, false); }}
                            />
                          ) : (
                            <IoBookmarkOutline
                              className={Styles.appActionIcon}
                              title="保存する"
                              onClick={(ev) => { ev.stopPropagation(); toggleSave(e.chatRoom.id, true); }}
                            />
                          )}
                        </span>
                        <span className={Styles.appColAction}>
                          <IoChatbubbleOutline
                            className={Styles.appActionIcon}
                            onClick={(ev) => { ev.stopPropagation(); openDetail(e.id, 'message'); }}
                          />
                          {e.chatRoom.company.badge > 0 && <span className={Styles.appActionIconDot} />}
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            )}
            </div>

            {selectedEntry && (
              <div className={Styles.workspaceDrawer}>
                <StudentDetailPanel
                  entry={selectedEntry}
                  initialTab={initialTab}
                  onClose={() => setSelectedId(null)}
                  onApplicantChanged={(patch) => applyApplicantPatch(selectedEntry.id, patch)}
                  onVisitStatusChanged={(status) => applyVisitStatusPatch(selectedEntry.id, status)}
                  closedPeriodName={closedPeriodName(selectedEntry)}
                  chatEvents={eventsByChat.get(selectedEntry.chatRoom.id) ?? []}
                />
              </div>
            )}
            </div>
          </div>
        )}
      </div>

      {memoToggle && (
        <MemoPanel memoToggle={memoToggle} setMemoToggle={() => setMemoToggle(null)} />
      )}

      {closingPeriod && (
        <ClosePeriodModal
          companyId={company.id}
          periodIndex={closingPeriod.index}
          periodName={closingPeriod.name}
          counts={closingPeriod.counts}
          alreadyClosed={closeTarget?.index !== closingPeriod.index}
          onClose={() => setClosingPeriod(null)}
          onClosed={() => {
            setClosingPeriod(null);
            setSelectedId(null);
            setApplicationPeriod(null);
          }}
        />
      )}
    </div>
  );
};

export default Evaluation;
